import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../Modal';
import { SortableTh } from '../SortableTh';
import { useTableSortState } from '../../hooks/useTableSort';
import { sortRows } from '../../utils/tableSort';
import { uploadTaskAttachment } from '../../api/tasks';
import {
  emptyMarketingOrderDraft,
  fetchMarketingOrders,
  submitMarketingOrder,
  type MarketingOrderDraft,
  type MarketingOrderRow,
} from '../../api/marketingOrders';

const LOGO_ACCEPT = '.png,.jpg,.jpeg,.svg,.ai,.eps,.pdf,image/*';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function truncate(value: string, max: number): string {
  if (!value) return '—';
  const flat = value.replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max)}…` : flat;
}

interface Props {
  userId: number;
  /** Prefills the contact block with the signed-in user. */
  defaultName?: string;
  defaultEmail?: string;
}

const MarketingOrderFormSection: React.FC<Props> = ({ userId, defaultName, defaultEmail }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<MarketingOrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [draft, setDraft] = useState<MarketingOrderDraft>(emptyMarketingOrderDraft);
  const [logoFiles, setLogoFiles] = useState<File[]>([]);
  const [graphicFiles, setGraphicFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [notice, setNotice] = useState('');
  const { sortKey, sortDir, toggleSort } = useTableSortState();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setOrders(await fetchMarketingOrders(userId));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load marketing orders.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sorted = useMemo(
    () =>
      sortRows(orders, sortKey, sortDir, (o, key) => {
        switch (key) {
          case 'submitted':
            return o.created_at ?? '';
          case 'requestedBy':
            return o.submitted_by_username ?? o.contact_name;
          case 'items':
            return o.items_requested;
          case 'turnaround':
            return o.turnaround_time;
          case 'proof':
            return o.proof_approval;
          case 'task':
            return o.task_status ?? '';
          default:
            return '';
        }
      }),
    [orders, sortKey, sortDir]
  );

  const set = <K extends keyof MarketingOrderDraft>(key: K, value: MarketingOrderDraft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const openForm = () => {
    setDraft({
      ...emptyMarketingOrderDraft(),
      contact_name: defaultName ?? '',
      contact_email: defaultEmail ?? '',
    });
    setLogoFiles([]);
    setGraphicFiles([]);
    setSubmitError('');
    setNotice('');
    setFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSubmitError('');
    const files = [...logoFiles, ...graphicFiles];
    try {
      const created = await submitMarketingOrder(
        userId,
        draft,
        files.map((f) => f.name)
      );
      // Design files ride along on the task so marketing has them in one place.
      const failed: string[] = [];
      if (created.task_id != null) {
        for (const file of files) {
          try {
            await uploadTaskAttachment(created.task_id, file, userId);
          } catch {
            failed.push(file.name);
          }
        }
      }
      setFormOpen(false);
      setNotice(
        failed.length > 0
          ? `Order submitted, but these files did not upload: ${failed.join(', ')}. Add them to the task directly.`
          : 'Order submitted. The marketing team has been notified.'
      );
      await load();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit marketing order.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="events-table-section" aria-labelledby="marketing-order-form-heading">
      <div className="marketing-order-header">
        <div>
          <h2 className="events-section-title" id="marketing-order-form-heading">
            Marketing Order Form <span className="events-section-count">({orders.length})</span>
          </h2>
          <p className="page-subtitle website-lead-intro">
            Request shirts, banners, flyers, stickers and other branded materials. Submitting
            notifies the marketing team and opens a task they can track.
          </p>
        </div>
        <button type="button" className="add-button marketing-order-new-btn" onClick={openForm}>
          + New order
        </button>
      </div>

      {notice ? (
        <p className="marketing-order-notice" role="status">
          {notice}
        </p>
      ) : null}
      {loadError ? (
        <p className="create-order-error" role="alert">
          {loadError}
        </p>
      ) : null}

      {loading ? (
        <p className="page-subtitle">Loading marketing orders…</p>
      ) : (
        <div className="events-table-wrap">
          <table className="tasks-search-results-table website-lead-table">
            <thead>
              <tr>
                <SortableTh label="Submitted" columnKey="submitted" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Requested by" columnKey="requestedBy" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Item(s)" columnKey="items" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <th>Quantity</th>
                <SortableTh label="Turnaround" columnKey="turnaround" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Proof" columnKey="proof" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                <SortableTh label="Task" columnKey="task" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={7} className="events-empty-cell">
                    No marketing orders yet. Use “New order” to place one.
                  </td>
                </tr>
              ) : (
                sorted.map((o) => (
                  <tr
                    key={o.id}
                    className={o.task_id != null ? 'events-table-row-clickable' : undefined}
                    onClick={o.task_id != null ? () => navigate(`/tasks/${o.task_id}`) : undefined}
                  >
                    <td data-label="Submitted">{formatDateTime(o.created_at)}</td>
                    <td data-label="Requested by">{o.submitted_by_username || o.contact_name || '—'}</td>
                    <td data-label="Item(s)">{truncate(o.items_requested, 60)}</td>
                    <td data-label="Quantity">{truncate(o.quantity_needed, 40)}</td>
                    <td data-label="Turnaround">{o.turnaround_time || '—'}</td>
                    <td data-label="Proof">{o.proof_approval}</td>
                    <td data-label="Task">
                      {o.task_id != null ? `#${o.task_id} — ${o.task_status ?? 'Unassigned'}` : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={formOpen}
        onClose={() => (submitting ? undefined : setFormOpen(false))}
        title="Marketing Order Form"
        wide
      >
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {submitError ? (
              <p className="create-order-error" role="alert">
                {submitError}
              </p>
            ) : null}

            <h4 className="website-lead-subheading">1. Contact Information</h4>
            <div className="marketing-order-grid">
              <label className="marketing-order-field">
                <span>Name *</span>
                <input
                  type="text"
                  value={draft.contact_name}
                  onChange={(e) => set('contact_name', e.target.value)}
                  required
                />
              </label>
              <label className="marketing-order-field">
                <span>Email address *</span>
                <input
                  type="email"
                  value={draft.contact_email}
                  onChange={(e) => set('contact_email', e.target.value)}
                  required
                />
              </label>
              <label className="marketing-order-field">
                <span>Phone number</span>
                <input
                  type="tel"
                  value={draft.contact_phone}
                  onChange={(e) => set('contact_phone', e.target.value)}
                />
              </label>
              <label className="marketing-order-field">
                <span>Company/organization name</span>
                <input
                  type="text"
                  value={draft.company_name}
                  onChange={(e) => set('company_name', e.target.value)}
                  placeholder="If applicable"
                />
              </label>
            </div>

            <h4 className="website-lead-subheading">2. Order Details</h4>
            <label className="marketing-order-field">
              <span>Item(s) requested *</span>
              <textarea
                rows={2}
                value={draft.items_requested}
                onChange={(e) => set('items_requested', e.target.value)}
                placeholder="e.g. T-shirts, banners, flyers, stickers"
                required
              />
            </label>
            <label className="marketing-order-field">
              <span>Quantity needed *</span>
              <textarea
                rows={2}
                value={draft.quantity_needed}
                onChange={(e) => set('quantity_needed', e.target.value)}
                placeholder="Specify the number of each item"
                required
              />
            </label>
            <div className="marketing-order-grid">
              <label className="marketing-order-field">
                <span>Turnaround time required</span>
                <input
                  type="text"
                  value={draft.turnaround_time}
                  onChange={(e) => set('turnaround_time', e.target.value)}
                  placeholder="e.g. Needed by March 14"
                />
                <small>Rush orders may incur additional fees.</small>
              </label>
              <label className="marketing-order-field">
                <span>Shipping time preference</span>
                <select
                  value={draft.shipping_time_preference}
                  onChange={(e) => set('shipping_time_preference', e.target.value)}
                >
                  <option value="">Select…</option>
                  <option value="Standard">Standard</option>
                  <option value="Expedited">Expedited</option>
                </select>
              </label>
            </div>
            <label className="marketing-order-field">
              <span>Shipping address</span>
              <textarea
                rows={3}
                value={draft.shipping_address}
                onChange={(e) => set('shipping_address', e.target.value)}
                placeholder="Include full address for delivery"
              />
            </label>

            <h4 className="website-lead-subheading">3. Design Specifications</h4>
            <div className="marketing-order-grid">
              <label className="marketing-order-field">
                <span>Company logo</span>
                <input
                  type="file"
                  accept={LOGO_ACCEPT}
                  multiple
                  onChange={(e) => setLogoFiles(Array.from(e.target.files ?? []))}
                />
                <small>High-resolution format, e.g. .png, .ai, .eps</small>
              </label>
              <label className="marketing-order-field">
                <span>Additional logos or graphics</span>
                <input
                  type="file"
                  accept={LOGO_ACCEPT}
                  multiple
                  onChange={(e) => setGraphicFiles(Array.from(e.target.files ?? []))}
                />
                <small>Anything else to include in the design</small>
              </label>
            </div>
            <label className="marketing-order-field">
              <span>Preferred colors</span>
              <input
                type="text"
                value={draft.preferred_colors}
                onChange={(e) => set('preferred_colors', e.target.value)}
                placeholder="Brand or team colors to be used"
              />
            </label>
            <label className="marketing-order-field">
              <span>Text or messaging</span>
              <textarea
                rows={2}
                value={draft.text_messaging}
                onChange={(e) => set('text_messaging', e.target.value)}
                placeholder="Specific text, slogans, or messaging to include"
              />
            </label>
            <label className="marketing-order-field">
              <span>Design notes</span>
              <textarea
                rows={3}
                value={draft.design_notes}
                onChange={(e) => set('design_notes', e.target.value)}
                placeholder="Special instructions or preferences for the design"
              />
            </label>

            <h4 className="website-lead-subheading">4. Approval Process</h4>
            <fieldset className="marketing-order-fieldset">
              <legend>Proof approval</legend>
              <label className="marketing-order-radio">
                <input
                  type="radio"
                  name="proof_approval"
                  checked={draft.proof_approval === 'Proof requested'}
                  onChange={() => set('proof_approval', 'Proof requested')}
                />
                <span>I would like to review a digital proof before production.</span>
              </label>
              <label className="marketing-order-radio">
                <input
                  type="radio"
                  name="proof_approval"
                  checked={draft.proof_approval === 'No proof needed'}
                  onChange={() => set('proof_approval', 'No proof needed')}
                />
                <span>No proof needed; proceed with production.</span>
              </label>
            </fieldset>

            <h4 className="website-lead-subheading">5. Payment Information</h4>
            <label className="marketing-order-field">
              <span>Payment method</span>
              <input
                type="text"
                value={draft.payment_method}
                onChange={(e) => set('payment_method', e.target.value)}
                placeholder="e.g. Credit Card, PayPal, Invoice"
              />
            </label>
            <label className="marketing-order-field">
              <span>Billing address</span>
              <textarea
                rows={3}
                value={draft.billing_address}
                onChange={(e) => set('billing_address', e.target.value)}
                placeholder="If different from shipping address"
              />
            </label>

            <h4 className="website-lead-subheading">6. Additional Notes</h4>
            <label className="marketing-order-field">
              <span>Special requests or comments</span>
              <textarea
                rows={3}
                value={draft.special_requests}
                onChange={(e) => set('special_requests', e.target.value)}
                placeholder="Any additional information or requests for your order"
              />
            </label>
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={() => setFormOpen(false)}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="update-button" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit order'}
            </button>
          </div>
        </form>
      </Modal>
    </section>
  );
};

export default MarketingOrderFormSection;
